import React, { useState, useEffect, useCallback } from 'react'
import ImmutablePropTypes from 'react-immutable-proptypes'
import PropTypes from 'prop-types'
import { List, Map } from 'immutable'
import compose from 'recompose/compose'
import FormsyForm from 'formsy-react'
import { connect as connectRedux } from 'react-redux'
import { connect as connectStaticText } from '../../StaticText/connect'
import { getBoundActions } from '../../../actions/index'
import Sherpa, { TYPE_SMALL_BLUE } from '../../Sherpa/index'
import { H5 } from '../../Heading/index'
import { TextInput, Checkbox } from '../../FormInput.v2/index'
import FeedbackBox, { FEEDBACK_BOX_TYPES } from '../../FeedbackBox/index'
import {
  getImmutableBasePlaylists,
  PLAYLIST_ERROR_EXISTING,
  formatPlaylistInputErrors,
} from '../../../services/playlist/index'
import Button, { TYPES } from '../../Button/index'

const MultiplePlaylistsAddModal = (props) => {
  const {
    createUserPlaylistAndAttachItem,
    resetNewPlaylistError,
    createUserPlaylist,
    clearPlaylistItem,
    processingCreate,
    getPlaylistItem,
    dismissModal,
    contentTitle,
    errorCreate,
    staticText,
    createOnly,
    playlists,
    hasPortal,
    contentId,
    onSelect,
    uniqueId,
    meta,
    uiLocation,
    uiElement,
  } = props

  const [validForm, setValidForm] = useState(false)
  const [inputVisible, setInputVisible] = useState(false)

  const disabled = !validForm
  const errorKey = errorCreate === PLAYLIST_ERROR_EXISTING ? 'otherNameMessage' : 'errorMessage'
  const titleTextKey = createOnly ? 'titleCreate' : 'titleAdd'

  const basePlaylists = getImmutableBasePlaylists(hasPortal)
  const allPlaylists = playlists.size ? basePlaylists.concat(playlists) : basePlaylists
  const metaPlaylists = meta.get('playlists', List())

  useEffect(() => {
    if (contentId) {
      getPlaylistItem({
        componentId: uniqueId,
        contentId,
      })
    }
    return () => {
      clearPlaylistItem({
        componentId: uniqueId,
        contentId,
      })
    }
  }, [uniqueId, contentId])

  const onValid = () => {
    setValidForm(true)
    if (errorCreate) {
      resetNewPlaylistError()
    }
  }

  const onCheckboxClick = useCallback((n, c) => {
    const pl = allPlaylists.find((l) => l.get('type') === n)
    const pn = pl.get('name')
    onSelect(n, c, contentId, pn, contentTitle)
    dismissModal()
  }, [contentId, contentTitle, allPlaylists])

  const onSubmit = (model) => {
    if (createOnly) {
      createUserPlaylist(
        model.newPlaylist,
        staticText.getIn(['data', 'successMessage']),
        true,
        uiLocation,
        uiElement,
      )
      return
    }
    createUserPlaylistAndAttachItem(
      model.newPlaylist,
      contentId,
      contentTitle,
      uiLocation,
      uiElement,
    )
  }

  const onInvalid = () => setValidForm(false)
  const onButtonClick = () => setInputVisible(true)

  const renderSubmitButton = () => {
    if (!inputVisible && !createOnly) {
      return null
    }

    if (processingCreate) {
      return <Sherpa type={TYPE_SMALL_BLUE} className={['playlist-empty__sherpa']} />
    }

    return (
      <Button
        type={TYPES.PRIMARY}
        submit
        disabled={disabled}
        stacked
        className="multiple-playlists-add-modal__button"
      >
        {staticText.getIn(['data', 'buttonSave'])}
      </Button>
    )
  }

  const renderCheckboxes = () => {
    if (createOnly) {
      return null
    }
    return allPlaylists.map((pl) => {
      const name = pl.get('staticTextKey') ? staticText.getIn(['data', pl.get('staticTextKey')]) : pl.get('name')
      const inPlaylist = !!metaPlaylists.find((metaPl) => metaPl.get('playlistType') === pl.get('type'))

      return (
        <Checkbox
          key={pl.get('id')}
          label={name}
          onChange={onCheckboxClick}
          value={inPlaylist}
          name={pl.get('type')}
          block
        />
      )
    })
  }

  return (
    <div className="multiple-playlists-add-modal">
      <FormsyForm
        className="multiple-playlists-add-modal"
        onValid={onValid}
        onInvalid={onInvalid}
        onValidSubmit={onSubmit}
      >
        <H5>{staticText.getIn(['data', titleTextKey])}</H5>
        {!createOnly ? <div className="multiple-playlists-add-modal__list">
          {allPlaylists.size && meta.size
            ? renderCheckboxes()
            : <Sherpa type={TYPE_SMALL_BLUE} className={['multiple-playlists-add-modal__sherpa']} />}
        </div> : null}
        {errorCreate && <FeedbackBox type={FEEDBACK_BOX_TYPES.ERROR}>
          {staticText.getIn(['data', errorKey])}
        </FeedbackBox>}
        {(inputVisible || createOnly) && <TextInput
          name={'newPlaylist'}
          label={staticText.getIn(['data', 'placeholder'])}
          validations={{
            maxLength: 40,
          }}
          validationErrors={{
            maxLength: staticText.getIn(['data', 'inputMaxLengthMessage']),
          }}
          validationErrorFormat={formatPlaylistInputErrors}
          maxLength={100}
          autocomplete="off"
          value={''}
          required
        />}
        {(!inputVisible && !createOnly) && <Button
            type={TYPES.SECONDARY}
            onClick={onButtonClick}
            stacked
            className="multiple-playlists-add-modal__button"
          >
            {staticText.getIn(['data', 'buttonCreate'])}
          </Button>
        }
        {renderSubmitButton()}
      </FormsyForm>
    </div>
  )
}

MultiplePlaylistsAddModal.propTypes = {
  staticText: ImmutablePropTypes.map.isRequired,
  playlists: ImmutablePropTypes.list.isRequired,
  meta: ImmutablePropTypes.map,
  createUserPlaylistAndAttachItem: PropTypes.func.isRequired,
  resetNewPlaylistError: PropTypes.func.isRequired,
  createOnly: PropTypes.bool.isRequired,
  uniqueId: PropTypes.string,
  contentId: PropTypes.number,
  hasPortal: PropTypes.bool,
  onSelect: PropTypes.func,
  processingCreate: PropTypes.bool,
  errorCreate: PropTypes.string,
}

MultiplePlaylistsAddModal.defaultProps = {
  createOnly: false,
  hasPortal: false,
}

export default compose(
  connectStaticText({ storeKey: 'multiplePlaylistsAddModal' }),
  connectRedux(
    (state, { contentId }) => {
      return {
        processingCreate: state.playlist.getIn(['userPlaylists', 'new', 'processing']),
        errorCreate: state.playlist.getIn(['userPlaylists', 'new', 'error']),
        playlists: state.playlist.getIn(['userPlaylists', 'data'], List()),
        meta: state.playlist.getIn(['items', contentId, 'data'], Map()),
      }
    },
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        createUserPlaylistAndAttachItem: actions.playlist.createUserPlaylistAndAttachItem,
        resetNewPlaylistError: actions.playlist.resetNewUserPlaylistError,
        createUserPlaylist: actions.playlist.createUserPlaylist,
        clearPlaylistItem: actions.playlist.clearPlaylistItem,
        getPlaylistItem: actions.playlist.getPlaylistItem,
        dismissModal: actions.dialog.dismissModal,
      }
    },
  ),
)(MultiplePlaylistsAddModal)
